import React from 'react';

import { uniqueId } from 'lodash';
import { useSnackbar, type SnackbarMessage } from 'notistack';

import type { ShowNotification } from './types';
import {
  DEFAULT_NOTI_POSITION,
  getPaperSnackbarVariant,
  getSnackbarAnchorOriginFromPosition,
} from './useNotifications/helpers';

import { PaperSnackbar } from '../../components/snackbars';

export const useNotifications = () => {
  const snackbarMethods = useSnackbar();

  const showNotification = React.useCallback<ShowNotification>(
    (options) => {
      const _position = options.position ?? DEFAULT_NOTI_POSITION;
      const snackbarAnchorOrigin =
        getSnackbarAnchorOriginFromPosition(_position);

      const variant = getPaperSnackbarVariant(options.type);

      const snackbarId = options.key ?? uniqueId();

      const commonOptions = {
        id: snackbarId,
        ...(options.key !== undefined && { key: options.key }),
        persist: options.persist ?? false,
        anchorOrigin: snackbarAnchorOrigin,
        ...(options.autoHideDuration !== undefined && {
          autoHideDuration: options.autoHideDuration,
        }),
        ...(options.preventDuplicate !== undefined && {
          preventDuplicate: options.preventDuplicate,
        }),
        ...(options.action !== undefined && { action: options.action }),
        ...(options.onClick !== undefined && { onClick: options.onClick }),
      };

      if (options.content) {
        // Custom render escape hatch: forward `title` as the notistack message
        // so the content fn's `message` argument keeps working.
        snackbarMethods.enqueueSnackbar(options.title as SnackbarMessage, {
          ...commonOptions,
          content: options.content,
        });
        return;
      }

      snackbarMethods.enqueueSnackbar(snackbarId, {
        ...commonOptions,
        content: (key) => (
          <PaperSnackbar
            key={key}
            id={key as string}
            message={options.title}
            variant={variant}
            titleIcon={options.icon}
          >
            {options.body}
          </PaperSnackbar>
        ),
      });
    },
    [snackbarMethods]
  );

  const success = React.useCallback(
    (title: React.ReactNode, body?: React.ReactNode) =>
      showNotification({ type: 'success', title, body }),
    [showNotification]
  );

  const error = React.useCallback(
    (title: React.ReactNode, body?: React.ReactNode) =>
      showNotification({ type: 'error', title, body }),
    [showNotification]
  );

  const info = React.useCallback(
    (title: React.ReactNode, body?: React.ReactNode) =>
      showNotification({ type: 'info', title, body }),
    [showNotification]
  );

  const warning = React.useCallback(
    (title: React.ReactNode, body?: React.ReactNode) =>
      showNotification({ type: 'warning', title, body }),
    [showNotification]
  );

  return React.useMemo(
    () => ({
      show: showNotification,
      success,
      error,
      info,
      warning,
    }),
    [error, info, showNotification, success, warning]
  );
};

export default useNotifications;
